import { useCallback, useState } from 'preact/hooks';
import { MAPS } from '@stillsun/shared';
import { api, qs } from '../lib/api.js';
import { useFetch } from '../lib/useFetch.js';
import type { DeathsResponse, TelemetrySummary } from '../lib/types.js';
import { BarChart, LineChart } from '../components/Chart.js';
import { StatTile } from '../components/StatTile.js';
import { DeathHeatmap } from '../components/DeathHeatmap.js';
import { formatDay, formatPct } from '../lib/format.js';

const MAP_IDS = Object.keys(MAPS);

export function Telemetry() {
  const fetchSummary = useCallback(
    () => api.get<TelemetrySummary>('/api/admin/telemetry/summary'),
    [],
  );
  const summary = useFetch(fetchSummary, []);
  const [mapId, setMapId] = useState(MAP_IDS[0] ?? 'yard');

  const fetchDeaths = useCallback(
    () => api.get<DeathsResponse>(`/api/admin/telemetry/deaths${qs({ mapId })}`),
    [mapId],
  );
  const deaths = useFetch(fetchDeaths, [mapId]);

  const weapons = [...(summary.data?.weapons ?? [])].sort((a, b) => b.shots - a.shots);
  const maxShots = Math.max(1, ...weapons.map((w) => w.shots));

  return (
    <div>
      <h1 class="page-title">Telemetry</h1>
      {summary.error && <div class="error-banner">{summary.error}</div>}

      <div class="tiles">
        <StatTile
          label="D1 retention"
          value={summary.data ? formatPct(summary.data.retention.d1) : '—'}
        />
        <StatTile
          label="D7 retention"
          value={summary.data ? formatPct(summary.data.retention.d7) : '—'}
        />
        <StatTile label="Players online" value={summary.data?.playersOnline ?? '—'} />
        <StatTile label="Sessions today" value={summary.data?.sessionsToday ?? '—'} />
      </div>

      <div class="charts-grid">
        <div class="card">
          <LineChart
            title="Daily active users"
            points={(summary.data?.dau ?? []).map((d) => ({
              label: formatDay(d.day),
              value: d.count,
            }))}
          />
        </div>
        <div class="card">
          <BarChart
            title="Raids per day"
            points={(summary.data?.raids ?? []).map((d) => ({
              label: formatDay(d.day),
              value: d.count,
            }))}
          />
        </div>
        <div class="card">
          <LineChart
            title="Extract rate"
            formatValue={(v) => `${Math.round(v)}%`}
            points={(summary.data?.raids ?? []).map((d) => ({
              label: formatDay(d.day),
              value: Math.round(d.extractRate * 100),
            }))}
          />
        </div>
      </div>

      <div class="card" style={{ marginBottom: 18 }}>
        <div class="section-label" style={{ marginTop: 0 }}>
          Weapon usage
        </div>
        {weapons.length === 0 && <div class="empty">No weapon telemetry yet.</div>}
        {weapons.length > 0 && (
          <table class="grid">
            <thead>
              <tr>
                <th>Weapon</th>
                <th>Shots</th>
                <th>Hits</th>
                <th>Accuracy</th>
                <th>Kills</th>
              </tr>
            </thead>
            <tbody>
              {weapons.map((w) => {
                const acc = w.shots > 0 ? w.hits / w.shots : 0;
                return (
                  <tr key={w.weaponId}>
                    <td>{w.weaponId}</td>
                    <td>
                      <span class="accbar">
                        <span
                          class="accbar-fill"
                          style={{ width: `${(w.shots / maxShots) * 100}%` }}
                        />
                      </span>
                      {w.shots}
                    </td>
                    <td>{w.hits}</td>
                    <td>{formatPct(acc)}</td>
                    <td>{w.kills}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div class="card">
        <div class="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
          <div class="section-label" style={{ margin: 0 }}>
            Death heatmap
          </div>
          <select
            class="select"
            style={{ width: 160 }}
            value={mapId}
            onChange={(e) => setMapId((e.target as HTMLSelectElement).value)}
          >
            {MAP_IDS.map((id) => (
              <option key={id} value={id}>
                {MAPS[id]?.name ?? id}
              </option>
            ))}
          </select>
        </div>
        {deaths.error && <div class="error-banner">{deaths.error}</div>}
        <DeathHeatmap mapId={mapId} deaths={deaths.data?.deaths ?? []} />
      </div>
    </div>
  );
}
